// Authenticated page — used both for first-login forced change
// (must_change_password === true) and for users self-rotating their password.

import { useState, type JSX, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, apiFetch } from "../lib/api.js";
import { FormField } from "../components/FormField.js";

// eslint-disable-next-line max-lines-per-function -- coherent React component / single-purpose hook; splitting would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function ChangePasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [currentPassword, setCurrent] = useState("");
  const [newPassword,     setNew]     = useState("");
  const [confirm,         setConfirm] = useState("");
  const [error,           setError]   = useState<string | null>(null);
  const [loading,         setLoading] = useState(false);

  // eslint-disable-next-line complexity, sonarjs/cognitive-complexity -- multi-step validation + submit flow; per-step extraction would shuffle without reducing total complexity. Tracked under TASK-N19 Phase B.
  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (newPassword !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await apiFetch("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      // Update the cached user object so the must_change_password gate clears.
      const cached = sessionStorage.getItem("hcm_user");
      if (cached) {
        try {
          const u = JSON.parse(cached) as { must_change_password?: boolean };
          u.must_change_password = false;
          sessionStorage.setItem("hcm_user", JSON.stringify(u));
        } catch { /* ignore */ }
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (err.status === 400 && body?.error) setError(body.error);
        else if (err.status === 422) setError("Password must be at least 8 characters.");
        else setError("Could not change password. Please try again.");
      } else {
        setError("Unable to connect.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <span className="text-2xl font-bold text-navy-700">HCM</span>
          <p className="mt-1 text-sm text-slate-500">Home Care Management</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Change password</h1>
          <p className="text-sm text-slate-500 mb-6">
            Choose a new password. You'll need to use it the next time you sign in.
          </p>
          {error && (
            <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}
          <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4" noValidate>
            <FormField
              id="current-password"
              type="password"
              label="Current password"
              value={currentPassword}
              onChange={setCurrent}
              autoComplete="current-password"
              required
            />
            <FormField
              id="new-password"
              type="password"
              label="New password"
              value={newPassword}
              onChange={setNew}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              required
            />
            <FormField
              id="confirm-password"
              type="password"
              label="Confirm new password"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? "Saving…" : "Change password"}
            </button>
            <Link to="/dashboard" className="block w-full text-center text-sm text-slate-500 hover:text-slate-700">
              Cancel
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
}
